import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { fetchData } from '../api/publicService';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  Container,
  Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

// --- react-leaflet components & setup ---
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import BackgroundImage from '../assets/images/vedha.png'; 
import PlaceholderImage from '../assets/images/57-536x354.jpg'; 
import 'leaflet/dist/leaflet.css';

// --- Leaflet Icon Fix ---
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
});
L.Marker.prototype.options.icon = DefaultIcon;

// --- Styled Components ---
const DestinationCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  transition: 'transform 0.35s ease, box-shadow 0.35s ease',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 32px rgba(0,0,0,0.15)',
    '& .action-overlay': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
}));

const ActionOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  padding: theme.spacing(2),
  background: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  transform: 'translateY(100%)',
  opacity: 0,
  transition: 'transform 0.35s ease, opacity 0.35s ease',
  backdropFilter: 'blur(5px)',
}));

const Destinations = () => {
  const { t } = useTranslation();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { language } = useLanguage();
  const locationRefs = useRef({});

  useEffect(() => {
    const loadDestinations = async () => {
      try {
        setLoading(true);
        const data = await fetchData('/destinations', language);
        setDestinations(data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError(t('destinations_fetch_error') || "Failed to load destinations.");
      } finally {
        setLoading(false);
      }
    };
    loadDestinations();
  }, [language, t]);

  const handleMarkerClick = (destinationId) => {
    const cardElement = locationRefs.current[destinationId];
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  if (error) return <Container maxWidth="md" sx={{ py: 8 }}><Alert severity="error">{error}</Alert></Container>;

  return (
    <Box>
      <Box sx={{ position: 'relative', width: '100vw', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', backgroundImage: `url(${BackgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center center', backgroundAttachment: 'fixed', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(252, 249, 244, 0.75)', zIndex: 1, }, }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: { xs: 6, md: 10 } }}>
          <Typography variant="h2" component="h1" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: { xs: '2.8rem', md: '4rem' }, lineHeight: 1.2, mb: '1.5rem', color: '#1A202C', textAlign: 'center' }}>
            {t('destinations_page_headline') || "Our Destinations"}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1.8, maxWidth: '700px', margin: '0 auto 4rem auto', color: '#2D3748', textAlign: 'center' }}>
            {t('destinations_page_subheadline') || "Explore the beauty of Ceylon"}
          </Typography>
          
          <Box sx={{ height: '500px', width: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: 5, mb: 2 }}>
    {/* MapContainer MUST have a defined height via its parent or style prop */}
    <MapContainer 
        center={[7.8731, 80.7718]} 
        zoom={8} 
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
    >
        <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {destinations.filter(dest => dest.latitude && dest.longitude).map((destination) => (
            <Marker 
                key={destination._id} 
                position={[destination.latitude, destination.longitude]}
                // Note: v4 uses standard event handlers
                eventHandlers={{ click: () => handleMarkerClick(destination._id) }}
            >
                <Popup>{destination.name}</Popup>
            </Marker>
        ))}
    </MapContainer>
</Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          {destinations.length === 0 ? (
            <Typography align="center" color="text.secondary">{t('destinations_not_found') || "No destinations found."}</Typography>
          ) : (
            <Grid container spacing={4}>
              {destinations.map((destination, index) => (
                // --- FIXED GRID SYNTAX HERE ---
                // Removed 'size={{...}}' and replaced with standard xs/sm/md props
                <Grid 
                  item 
                  key={destination._id} 
                  size={{ xs: 12, sm: 6, md: 4 }}
                  ref={(el) => (locationRefs.current[destination._id] = el)}
                >
                  <Fade in={true} timeout={500 + index * 100}>
                    <Link to={`/destinations/${destination.slug}`} style={{ textDecoration: 'none' }}>
                      <DestinationCard>
                        <CardMedia
                          component="img"
                          image={destination.images?.[0] || PlaceholderImage}
                          alt={destination.name}
                          sx={{ height: 250, objectFit: 'cover' }}
                        />
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          <Typography variant="h5" component="h2" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
                            {destination.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, height: '60px', overflow: 'hidden' }}>
                            {destination.shortDescription}
                          </Typography>
                        </CardContent>
                        <ActionOverlay className="action-overlay">
                          <Typography variant="button">{t('destinations_card_view_details') || "View Details"}</Typography>
                          <ArrowForwardIcon />
                        </ActionOverlay>
                      </DestinationCard>
                    </Link>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default Destinations;